import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Brand } from "./AppHeader";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft,
  CreditCard,
  FileSpreadsheet,
  LayoutDashboard,
  LogOut,
  Package,
  Percent,
  Tags,
  Users,
} from "lucide-react";

const itens = [
  { to: "/admin", label: "Painel", icon: LayoutDashboard, exato: true },
  { to: "/admin/produtos", label: "Produtos", icon: Package },
  { to: "/admin/impostos", label: "Impostos (ST/DIFAL)", icon: Percent },
  { to: "/admin/ncms", label: "NCMs", icon: Tags },
  { to: "/admin/importacao", label: "Importar planilha", icon: FileSpreadsheet },
  { to: "/admin/condicoes", label: "Cond. de pagamento", icon: CreditCard },
  { to: "/admin/usuarios", label: "Usuários", icon: Users },
];

export const AdminShell = ({ titulo, descricao, acoes, children }) => {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-14 max-w-[1500px] items-center gap-3 px-3 sm:px-5">
          <Brand compact />
          <Badge variant="secondary" className="bg-slate-900 text-[10px] uppercase text-white">
            Administração
          </Badge>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/consulta")}
              data-testid="admin-voltar-consulta"
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Portal do representante
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-sair">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1500px] gap-0 px-0 lg:px-5">
        <aside className="hidden w-60 shrink-0 border-r border-slate-200 bg-white lg:block">
          <nav className="sticky top-14 space-y-1 p-3">
            {itens.map((i) => {
              const ativo = i.exato ? pathname === i.to : pathname.startsWith(i.to);
              return (
                <Link
                  key={i.to}
                  to={i.to}
                  data-testid={`admin-nav-${i.to.split("/").pop() || "painel"}`}
                >
                  <span
                    className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-colors ${
                      ativo
                        ? "bg-primary/10 text-primary"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    <i.icon className="h-4 w-4" />
                    {i.label}
                  </span>
                </Link>
              );
            })}
            <div className="mt-4 rounded-lg bg-slate-50 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Conectado como
              </div>
              <div className="truncate text-[13px] font-medium text-slate-900">{user?.nome}</div>
              <div className="truncate text-[11px] text-slate-500">{user?.email}</div>
            </div>
          </nav>
        </aside>

        <main className="min-w-0 flex-1 px-3 py-5 sm:px-5">
          {/* nav mobile */}
          <nav className="mb-4 flex gap-1.5 overflow-x-auto lg:hidden">
            {itens.map((i) => {
              const ativo = i.exato ? pathname === i.to : pathname.startsWith(i.to);
              return (
                <Link key={i.to} to={i.to} className="shrink-0">
                  <span
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[12.5px] font-medium ${
                      ativo
                        ? "border-primary/30 bg-primary/10 text-primary"
                        : "border-slate-200 bg-white text-slate-600"
                    }`}
                  >
                    <i.icon className="h-3.5 w-3.5" />
                    {i.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="font-display text-[21px] font-bold tracking-tight text-slate-900">
                {titulo}
              </h1>
              {descricao && <p className="mt-0.5 text-[13px] text-slate-600">{descricao}</p>}
            </div>
            {acoes && <div className="flex flex-wrap gap-2">{acoes}</div>}
          </div>

          {children}
        </main>
      </div>
    </div>
  );
};
